import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { PRESIGNED_URL_MAX_COUNT } from '../api/client';
import './DVCViewer.css';
import {
  type DatasetData,
  type BatchProgress,
  type OverlayMode,
  type OverlaySelection,
  type DvcClassificationIndexBundle,
  type DvcWeightIndex,
  type DvcBboxIndex,
  drawBoundingBoxes,
  setupBoundingBoxOverlay as setupBboxOverlay,
  DatasetVersionSelect,
  ImageDetailPanel,
  SampleImagesSection,
  BatchProcessingPanel,
} from './dvc';

const getDataviewerApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:8000/api';
  return '/api';
};

function mergeClassificationListsUniqueSorted(a: string[], b: string[]): string[] {
  return Array.from(
    new Set(
      [...a, ...b]
        .map((x) => String(x).trim())
        .filter((x) => x.length > 0)
    )
  ).sort((x, y) => x.localeCompare(y));
}

function recomputeMergedFromCsvDb(
  csv: Record<string, string[]>,
  db: Record<string, string[]>
): Record<string, string[]> {
  const merged: Record<string, string[]> = {};
  for (const u of Object.keys(csv)) {
    merged[u] = mergeClassificationListsUniqueSorted(csv[u] || [], db[u] || []);
  }
  return merged;
}

const DVCViewer: React.FC = () => {
  const [datasets, setDatasets] = useState<string[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<string>('');
  const [versions, setVersions] = useState<string[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [datasetData, setDatasetData] = useState<DatasetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [allSampleUrls, setAllSampleUrls] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [imagesPerPage] = useState(20);
  const [useInfiniteScroll, setUseInfiniteScroll] = useState(false);
  const [visibleImageCount, setVisibleImageCount] = useState(50);
  const [showSampleData, setShowSampleData] = useState(false);
  const [showBatchProcessing, setShowBatchProcessing] = useState(false);
  const [batchSize, setBatchSize] = useState(20);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const [classificationInput, setClassificationInput] = useState('');
  const [savingClassifications, setSavingClassifications] = useState(false);
  const [selectedImageUrls, setSelectedImageUrls] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);
  const [classificationBatchInput, setClassificationBatchInput] = useState('');
  const [addingClassificationsBatch, setAddingClassificationsBatch] = useState(false);
  const [overlay, setOverlay] = useState<OverlaySelection>({
    bboxes: true,
    classifications: false,
    keypoints: false,
    weights: false,
  });
  const [classificationToRemove, setClassificationToRemove] = useState('');
  const [removingClassification, setRemovingClassification] = useState(false);
  const [imageProcessingMode] = useState('presigned');
  const [dvcPresignedThumbMap, setDvcPresignedThumbMap] = useState<Map<string, string> | null>(
    () => new Map()
  );
  const [classificationIndexBundle, setClassificationIndexBundle] =
    useState<DvcClassificationIndexBundle | null>(null);
  const [classificationIndexLoading, setClassificationIndexLoading] = useState(false);
  const [classificationIndexError, setClassificationIndexError] = useState('');
  const [weightIndex, setWeightIndex] = useState<DvcWeightIndex | null>(null);
  const [weightIndexLoading, setWeightIndexLoading] = useState(false);
  const [bboxIndex, setBboxIndex] = useState<DvcBboxIndex | null>(null);
  const [bboxIndexLoading, setBboxIndexLoading] = useState(false);
  const [hasWeights, setHasWeights] = useState(false);
  const [visibleBboxClasses, setVisibleBboxClasses] = useState<Set<string>>(new Set());
  const [selectedClassificationFilters, setSelectedClassificationFilters] = useState<string[]>(
    []
  );
  const [includeLabelsFromCsv, setIncludeLabelsFromCsv] = useState(true);
  const [includeLabelsFromDb, setIncludeLabelsFromDb] = useState(true);

  const effectiveClassificationIndex = React.useMemo(() => {
    if (!classificationIndexBundle) return null;
    const { results_csv, results_db, results_merged } = classificationIndexBundle;
    if (includeLabelsFromCsv && includeLabelsFromDb) return results_merged;
    if (includeLabelsFromCsv && !includeLabelsFromDb) return results_csv;
    if (!includeLabelsFromCsv && includeLabelsFromDb) return results_db;
    return results_merged;
  }, [classificationIndexBundle, includeLabelsFromCsv, includeLabelsFromDb]);

  const allClassificationNames = React.useMemo(() => {
    if (!effectiveClassificationIndex) return [];
    const names = new Set<string>();
    for (const arr of Object.values(effectiveClassificationIndex)) {
      for (const c of arr) {
        if (c && String(c).trim()) names.add(String(c).trim());
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [effectiveClassificationIndex]);

  const canvasRefs = useRef<{ [key: string]: HTMLCanvasElement | null }>({});
  const imageRefs = useRef<{ [key: string]: HTMLImageElement | null }>({});
  const batchProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const presignedMapRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    loadDatasets();
    return () => {
      if (batchProgressIntervalRef.current) {
        clearInterval(batchProgressIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDataset) {
      loadVersions(selectedDataset);
    } else {
      setVersions([]);
      setSelectedVersion('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataset]);

  useEffect(() => {
    if (selectedDataset && selectedVersion) {
      loadSampleData();
    } else {
      setAllSampleUrls([]);
      setShowSampleData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataset, selectedVersion]);

  useEffect(() => {
    presignedMapRef.current = dvcPresignedThumbMap ?? new Map();
  }, [dvcPresignedThumbMap]);

  useEffect(() => {
    if (imageProcessingMode === 'direct_s3') {
      setDvcPresignedThumbMap(new Map());
    } else {
      setDvcPresignedThumbMap(null);
    }
  }, [selectedDataset, selectedVersion, imageProcessingMode]);

  useEffect(() => {
    setCurrentPage(0);
    setVisibleImageCount(50);
    setSelectedClassificationFilters([]);
    setIncludeLabelsFromCsv(true);
    setIncludeLabelsFromDb(true);
  }, [selectedDataset, selectedVersion]);

  useEffect(() => {
    if (!selectedDataset || !selectedVersion || allSampleUrls.length === 0) {
      setClassificationIndexBundle(null);
      setClassificationIndexLoading(false);
      setClassificationIndexError('');
      return;
    }
    let cancelled = false;
    setClassificationIndexLoading(true);
    setClassificationIndexError('');
    api.dvcviewer
      .getClassificationIndex(selectedDataset, selectedVersion)
      .then((res) => {
        if (cancelled) return;
        setClassificationIndexBundle({
          results_csv: res.data.results_csv || {},
          results_db: res.data.results_db || {},
          results_merged: res.data.results_merged || {},
        });
      })
      .catch((err: any) => {
        if (!cancelled) {
          setClassificationIndexBundle(null);
          setClassificationIndexError(
            err.response?.data?.detail || err.message || 'Failed to load classification index'
          );
          setSelectedClassificationFilters([]);
        }
      })
      .finally(() => {
        if (!cancelled) setClassificationIndexLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDataset, selectedVersion, allSampleUrls.length]);

  useEffect(() => {
    if (!selectedDataset || !selectedVersion || allSampleUrls.length === 0) {
      setWeightIndex(null);
      setWeightIndexLoading(false);
      setHasWeights(false);
      return;
    }
    let cancelled = false;
    setWeightIndexLoading(true);
    api.dvcviewer
      .getWeightIndex(selectedDataset, selectedVersion)
      .then((res) => {
        if (cancelled) return;
        const results = res.data.results || {};
        setWeightIndex(results);
        setHasWeights((res.data.num_with_weight || 0) > 0);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setWeightIndex(null);
          setHasWeights(false);
          console.error('Failed to load weight index:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setWeightIndexLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDataset, selectedVersion, allSampleUrls.length]);

  useEffect(() => {
    if (!selectedDataset || !selectedVersion || allSampleUrls.length === 0) {
      setBboxIndex(null);
      setBboxIndexLoading(false);
      return;
    }
    let cancelled = false;
    setBboxIndexLoading(true);
    api.dvcviewer
      .getBboxIndex(selectedDataset, selectedVersion)
      .then((res) => {
        if (cancelled) return;
        setBboxIndex((res.data.results || {}) as DvcBboxIndex);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setBboxIndex(null);
          console.error('Failed to load bbox index:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setBboxIndexLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDataset, selectedVersion, allSampleUrls.length]);

  useEffect(() => {
    if (!hasWeights) {
      setOverlay((prev) => (prev.weights ? { ...prev, weights: false } : prev));
    }
  }, [hasWeights]);

  const toggleOverlay = (mode: OverlayMode) => {
    setOverlay((prev) => ({ ...prev, [mode]: !prev[mode] }));
  };

  useEffect(() => {
    setCurrentPage(0);
    setVisibleImageCount(50);
    setSelectedImageUrls(new Set());
    setLastSelectedIndex(-1);
  }, [selectedClassificationFilters, includeLabelsFromCsv, includeLabelsFromDb]);

  useEffect(() => {
    if (currentBatchId && batchProgress) {
      if (batchProgress.status === 'completed' || batchProgress.status === 'failed') {
        if (batchProgressIntervalRef.current) {
          clearInterval(batchProgressIntervalRef.current);
          batchProgressIntervalRef.current = null;
        }
      }
    }
  }, [batchProgress, currentBatchId]);

  const loadDatasets = async () => {
    try {
      const response = await api.dvcviewer.getDatasets();
      setDatasets(response.data.datasets);
      if (response.data.datasets.length > 0 && !selectedDataset) {
        setSelectedDataset(response.data.datasets[0]);
      }
    } catch (err: any) {
      setError(`Failed to load datasets: ${err.response?.data?.detail || err.message}`);
    }
  };

  const loadVersions = async (datasetName: string) => {
    try {
      const response = await api.dvcviewer.getVersions(datasetName);
      setVersions(response.data.versions);
      if (response.data.versions.length > 0 && !selectedVersion) {
        setSelectedVersion(response.data.versions[0]);
      }
    } catch (err: any) {
      setError(`Failed to load versions: ${err.response?.data?.detail || err.message}`);
    }
  };

  const loadSampleData = async () => {
    if (!selectedDataset || !selectedVersion) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.dvcviewer.getSampleData(selectedDataset, selectedVersion);
      setAllSampleUrls(response.data.sample_urls || []);
      setShowSampleData(true);
    } catch (err: any) {
      setError(`Failed to load sample data: ${err.response?.data?.detail || err.message}`);
      setAllSampleUrls([]);
      setShowSampleData(false);
    } finally {
      setLoading(false);
    }
  };

  const loadImageData = async (url: string) => {
    if (!url) return;
    setLoading(true);
    setDatasetData(null);
    setVisibleBboxClasses(new Set());
    setError('');
    try {
      const response = await api.dvcviewer.getImageData(selectedDataset, selectedVersion, url);
      setDatasetData(response.data);
      const classNames = Array.from(
        new Set(
          (response.data.ground_truth_labels || [])
            .filter((l) => l.bbox)
            .map((l) => l.class_name || (l.class_id != null ? `Class ${l.class_id}` : 'unknown'))
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));
      setVisibleBboxClasses(new Set(classNames));
    } catch (err: any) {
      setError(`Failed to load image data: ${err.response?.data?.detail || err.message}`);
      setDatasetData(null);
    } finally {
      setLoading(false);
    }
  };

  const bboxClassNames = React.useMemo(() => {
    if (!datasetData?.ground_truth_labels) return [];
    return Array.from(
      new Set(
        datasetData.ground_truth_labels
          .filter((l) => l.bbox)
          .map((l) => l.class_name || (l.class_id != null ? `Class ${l.class_id}` : 'unknown'))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [datasetData]);

  const toggleBboxClass = (className: string) => {
    setVisibleBboxClasses((prev) => {
      const next = new Set(prev);
      if (next.has(className)) next.delete(className);
      else next.add(className);
      return next;
    });
  };

  const setAllBboxClassesVisible = (visible: boolean) => {
    setVisibleBboxClasses(visible ? new Set(bboxClassNames) : new Set());
  };

  const setupBoundingBoxOverlay = useCallback(
    (uniqueId: string, data: DatasetData) => {
      const img = imageRefs.current[uniqueId];
      const canvas = canvasRefs.current[uniqueId];
      if (!img || !canvas) return;
      setupBboxOverlay(img, canvas, data, overlay, {
        showLabels: true,
        visibleClasses: visibleBboxClasses,
      });
    },
    [overlay, visibleBboxClasses]
  );

  const loadImageUrl = (url: string) => {
    loadImageData(url);
  };

  const currentClassificationNames = React.useMemo(() => {
    if (!datasetData?.ground_truth_labels) return [];
    return datasetData.ground_truth_labels
      .filter((l) => l.type === 'classification' || !l.bbox)
      .map((l) => l.class_name);
  }, [datasetData]);

  const addClassification = async () => {
    const label = classificationInput.trim();
    const imageUrl = datasetData?.image_url;
    if (!label || !datasetData || savingClassifications || !imageUrl) return;
    setSavingClassifications(true);
    setError('');
    try {
      const newList = [...currentClassificationNames, label];
      await api.dataviewer.addClassifications({
        image_url: imageUrl,
        classifications: newList,
      });
      setClassificationInput('');
      setClassificationIndexBundle((prev) => {
        if (!prev) return prev;
        const csv = { ...prev.results_csv };
        const db = { ...prev.results_db };
        const nextDb = Array.from(
          new Set(newList.map((x) => String(x).trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        db[imageUrl] = nextDb;
        return {
          results_csv: csv,
          results_db: db,
          results_merged: recomputeMergedFromCsvDb(csv, db),
        };
      });
      if (selectedDataset && selectedVersion && datasetData.image_url) {
        const res = await api.dvcviewer.getImageData(
          selectedDataset,
          selectedVersion,
          datasetData.image_url
        );
        if (res.data?.ground_truth_labels != null) {
          setDatasetData((prev) =>
            prev ? { ...prev, ground_truth_labels: res.data!.ground_truth_labels! } : null
          );
        }
      } else {
        const res = await api.dataviewer.getQueryImageData(datasetData.image_url);
        if (res.data?.ground_truth_labels != null) {
          setDatasetData((prev) =>
            prev ? { ...prev, ground_truth_labels: res.data!.ground_truth_labels! } : null
          );
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add classification');
    } finally {
      setSavingClassifications(false);
    }
  };

  const removeClassification = async (classToRemove: string) => {
    if (!datasetData || savingClassifications) return;
    const imageUrl = datasetData.image_url;
    setSavingClassifications(true);
    setError('');
    try {
      const newList = currentClassificationNames.filter((c) => c !== classToRemove);
      await api.dataviewer.addClassifications({
        image_url: imageUrl,
        classifications: newList,
      });
      setClassificationIndexBundle((prev) => {
        if (!prev) return prev;
        const csv = { ...prev.results_csv };
        const db = { ...prev.results_db };
        const nextDb = Array.from(
          new Set(newList.map((x) => String(x).trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        db[imageUrl] = nextDb;
        return {
          results_csv: csv,
          results_db: db,
          results_merged: recomputeMergedFromCsvDb(csv, db),
        };
      });
      if (selectedDataset && selectedVersion && datasetData.image_url) {
        const res = await api.dvcviewer.getImageData(
          selectedDataset,
          selectedVersion,
          datasetData.image_url
        );
        if (res.data?.ground_truth_labels != null) {
          setDatasetData((prev) =>
            prev ? { ...prev, ground_truth_labels: res.data!.ground_truth_labels! } : null
          );
        }
      } else {
        const res = await api.dataviewer.getQueryImageData(datasetData.image_url);
        if (res.data?.ground_truth_labels != null) {
          setDatasetData((prev) =>
            prev ? { ...prev, ground_truth_labels: res.data!.ground_truth_labels! } : null
          );
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove classification');
    } finally {
      setSavingClassifications(false);
    }
  };

  const filteredSampleUrls = React.useMemo(() => {
    if (selectedClassificationFilters.length === 0) return allSampleUrls;
    if (!effectiveClassificationIndex) return allSampleUrls;
    const want = new Set(selectedClassificationFilters);
    return allSampleUrls.filter((url) => {
      const labs = effectiveClassificationIndex[url] || [];
      return labs.some((c) => want.has(c));
    });
  }, [allSampleUrls, selectedClassificationFilters, effectiveClassificationIndex]);

  const handleImageSelect = (url: string, globalIndex: number, event: React.MouseEvent) => {
    if (!overlay.classifications) {
      loadImageUrl(url);
      return;
    }
    const newSelected = new Set(selectedImageUrls);
    if (event.shiftKey && lastSelectedIndex >= 0) {
      const startIdx = Math.min(lastSelectedIndex, globalIndex);
      const endIdx = Math.max(lastSelectedIndex, globalIndex);
      for (let i = startIdx; i <= endIdx; i++) {
        if (i < filteredSampleUrls.length) newSelected.add(filteredSampleUrls[i]);
      }
    } else {
      if (newSelected.has(url)) newSelected.delete(url);
      else newSelected.add(url);
      setLastSelectedIndex(globalIndex);
    }
    setSelectedImageUrls(newSelected);
  };

  useEffect(() => {
    if (!overlay.classifications) {
      setSelectedImageUrls(new Set());
      setLastSelectedIndex(-1);
    }
  }, [overlay.classifications]);

  const selectedImagesClassificationsUnion = React.useMemo(() => {
    if (!effectiveClassificationIndex) return [];
    const set = new Set<string>();
    Array.from(selectedImageUrls).forEach((url) => {
      (effectiveClassificationIndex[url] || []).forEach((c: string) => set.add(c));
    });
    return Array.from(set).sort();
  }, [selectedImageUrls, effectiveClassificationIndex]);

  const handleRemoveClassificationBatch = async () => {
    const rm = classificationToRemove.trim();
    if (!rm || selectedImageUrls.size === 0) return;
    setRemovingClassification(true);
    setError('');
    try {
      const res = await api.dataviewer.removeClassification({
        image_urls: Array.from(selectedImageUrls),
        classification: rm,
      });
      const data = res.data;
      if (data.errors && data.errors.length > 0) {
        setError(
          `Remove classification: ${data.successful_updates} updated; errors: ${data.errors.join('; ')}`
        );
      } else {
        setClassificationToRemove('');
        if (data.successful_updates !== undefined && data.successful_updates > 0) {
          setClassificationIndexBundle((prev) => {
            if (!prev) return prev;
            const csv = { ...prev.results_csv };
            const db = { ...prev.results_db };
            for (const u of Array.from(selectedImageUrls)) {
              if (!db[u]) continue;
              db[u] = db[u].filter((c) => c !== rm);
            }
            return {
              results_csv: csv,
              results_db: db,
              results_merged: recomputeMergedFromCsvDb(csv, db),
            };
          });
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove classification');
    } finally {
      setRemovingClassification(false);
    }
  };

  const handleAddClassificationsBatch = async () => {
    const raw = classificationBatchInput
      .split(/\s*,\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (raw.length === 0 || selectedImageUrls.size === 0) {
      if (selectedImageUrls.size === 0) setError('Select at least one image');
      return;
    }
    setAddingClassificationsBatch(true);
    setError('');
    try {
      const res = await api.dataviewer.addClassifications({
        image_urls: Array.from(selectedImageUrls),
        classifications: raw,
      });
      const data = res.data as {
        successful_updates?: number;
        total_records?: number;
        errors?: string[];
      };
      const count = data.successful_updates ?? data.total_records ?? 0;
      if (count > 0) {
        setClassificationBatchInput('');
        const urls = Array.from(selectedImageUrls);
        setClassificationIndexBundle((prev) => {
          if (!prev) return prev;
          const csv = { ...prev.results_csv };
          const db = { ...prev.results_db };
          for (const u of urls) {
            db[u] = mergeClassificationListsUniqueSorted(db[u] || [], raw);
          }
          return {
            results_csv: csv,
            results_db: db,
            results_merged: recomputeMergedFromCsvDb(csv, db),
          };
        });
        if (data.errors?.length) {
          setError(`Added to ${count} images. Some errors: ${data.errors.slice(0, 3).join('; ')}`);
        }
      } else if (data.errors?.length) {
        setError(data.errors.slice(0, 5).join('; '));
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add classification');
    } finally {
      setAddingClassificationsBatch(false);
    }
  };

  const goToPage = (page: number) => {
    const totalPagesCalc = Math.ceil(filteredSampleUrls.length / imagesPerPage);
    if (page >= 0 && page < totalPagesCalc) setCurrentPage(page);
  };

  const startBatchProcessing = async () => {
    try {
      if (!filteredSampleUrls?.length) {
        alert('No sample images available for batch processing');
        return;
      }
      const imageUrls = filteredSampleUrls.slice(0, batchSize);
      const response = await api.dvcviewer.startBatch({
        dataset_name: selectedDataset,
        version: selectedVersion,
        image_urls: imageUrls,
      });
      setCurrentBatchId(response.data.batch_id);
      setShowBatchProcessing(true);
      startBatchProgressMonitoring(response.data.batch_id);
    } catch (error: any) {
      console.error('Error starting batch processing:', error);
      alert('Failed to start batch processing: ' + (error.message || 'Unknown error'));
    }
  };

  const stopBatchProcessing = async () => {
    try {
      await api.dvcviewer.stopBatch();
      if (batchProgressIntervalRef.current) {
        clearInterval(batchProgressIntervalRef.current);
        batchProgressIntervalRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping batch processing:', err);
    }
  };

  const startBatchProgressMonitoring = (batchId: string) => {
    if (batchProgressIntervalRef.current) clearInterval(batchProgressIntervalRef.current);
    batchProgressIntervalRef.current = setInterval(async () => {
      try {
        const response = await api.dvcviewer.getBatchProgress(batchId);
        setBatchProgress(response.data);
        if (
          response.data.status === 'completed' ||
          response.data.status === 'failed'
        ) {
          if (batchProgressIntervalRef.current) {
            clearInterval(batchProgressIntervalRef.current);
            batchProgressIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.error('Error getting batch progress:', err);
      }
    }, 1000);
  };

  const totalPages = Math.ceil(filteredSampleUrls.length / imagesPerPage);
  const paginatedStartIndex = currentPage * imagesPerPage;
  const paginatedEndIndex = Math.min(
    paginatedStartIndex + imagesPerPage,
    filteredSampleUrls.length
  );
  const displayedUrls = React.useMemo(() => {
    if (useInfiniteScroll) {
      return filteredSampleUrls.slice(0, visibleImageCount);
    }
    return filteredSampleUrls.slice(paginatedStartIndex, paginatedEndIndex);
  }, [
    useInfiniteScroll,
    filteredSampleUrls,
    visibleImageCount,
    paginatedStartIndex,
    paginatedEndIndex,
  ]);
  const gridStartIndex = useInfiniteScroll ? 0 : paginatedStartIndex;
  const startIndex = paginatedStartIndex;
  const endIndex = paginatedEndIndex;
  const uniqueId = datasetData?.image_url
    ? `dvc-${datasetData.image_url.replace(/[^a-zA-Z0-9-_]/g, '_')}`
    : '';

  const thumbnailDisplayMode: 'direct_s3' | 'presigned' =
    imageProcessingMode === 'direct_s3' ? 'direct_s3' : 'presigned';

  useEffect(() => {
    if (!useInfiniteScroll || !imageContainerRef.current) return;
    const container = imageContainerRef.current;
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        if (visibleImageCount < filteredSampleUrls.length) {
          setVisibleImageCount((prev) => Math.min(prev + 50, filteredSampleUrls.length));
        }
      }
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [useInfiniteScroll, visibleImageCount, filteredSampleUrls.length]);

  useEffect(() => {
    if (useInfiniteScroll) {
      setVisibleImageCount(50);
    } else {
      setCurrentPage(0);
    }
  }, [useInfiniteScroll, filteredSampleUrls.length]);

  useEffect(() => {
    if (thumbnailDisplayMode === 'direct_s3') {
      setDvcPresignedThumbMap(new Map());
      return;
    }
    const s3List = displayedUrls.filter((u) => u.startsWith('s3://'));
    if (s3List.length === 0) {
      setDvcPresignedThumbMap(new Map());
      return;
    }
    const existing = presignedMapRef.current;
    const missing = s3List.filter((u) => !existing.has(u));
    if (missing.length === 0) return;

    const fullReload = missing.length === s3List.length;
    if (fullReload) {
      setDvcPresignedThumbMap(null);
    }

    const toFetch = fullReload ? s3List : missing;
    let cancelled = false;
    (async () => {
      try {
        const chunks: string[][] = [];
        for (let i = 0; i < toFetch.length; i += PRESIGNED_URL_MAX_COUNT) {
          chunks.push(toFetch.slice(i, i + PRESIGNED_URL_MAX_COUNT));
        }
        const responses = await Promise.all(
          chunks.map((chunk) => api.dataviewer.getPresignedUrls(chunk))
        );
        if (cancelled) return;
        const fetched = new Map<string, string>();
        for (const res of responses) {
          for (const item of res.data.urls) {
            if (item.presigned_url && item.s3_url) {
              fetched.set(item.s3_url, item.presigned_url);
            }
          }
        }
        setDvcPresignedThumbMap((prev) => {
          const base = fullReload ? new Map() : new Map(prev ?? new Map());
          fetched.forEach((v, k) => {
            base.set(k, v);
          });
          return base;
        });
      } catch (e) {
        console.error('DVC presigned URL batch failed:', e);
        if (!cancelled) {
          setDvcPresignedThumbMap((prev) =>
            fullReload ? new Map() : new Map(prev ?? new Map())
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [thumbnailDisplayMode, displayedUrls]);

  useEffect(() => {
    if (!uniqueId || !datasetData) return;
    const canvas = canvasRefs.current[uniqueId];
    if (!canvas) return;
    drawBoundingBoxes(canvas, datasetData, overlay, {
      showLabels: true,
      visibleClasses: visibleBboxClasses,
    });
  }, [overlay, uniqueId, datasetData, visibleBboxClasses]);

  const onIncludeLabelsFromCsvChange = (checked: boolean) => {
    if (!checked && !includeLabelsFromDb) return;
    setIncludeLabelsFromCsv(checked);
  };

  const onIncludeLabelsFromDbChange = (checked: boolean) => {
    if (!checked && !includeLabelsFromCsv) return;
    setIncludeLabelsFromDb(checked);
  };

  return (
    <div className="dvc-container">
      <h2 style={{ marginBottom: '2rem' }}>Picsellia Dataset Manager</h2>

      {error && (
        <div className="dvc-error-message">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}

      <DatasetVersionSelect
        datasets={datasets}
        selectedDataset={selectedDataset}
        onDatasetChange={setSelectedDataset}
        versions={versions}
        selectedVersion={selectedVersion}
        onVersionChange={setSelectedVersion}
      />

      {loading && (
        <div className="dvc-loading-message">
          <div>Loading...</div>
        </div>
      )}

      {datasetData && (
        <ImageDetailPanel
          datasetData={datasetData}
          overlay={overlay}
          onToggleOverlay={toggleOverlay}
          uniqueId={uniqueId}
          canvasRef={(el) => {
            canvasRefs.current[uniqueId] = el;
          }}
          imageRef={(el) => {
            imageRefs.current[uniqueId] = el;
          }}
          onImageLoad={() => {
            if (datasetData) setupBoundingBoxOverlay(uniqueId, datasetData);
          }}
          currentClassificationNames={currentClassificationNames}
          classificationInput={classificationInput}
          setClassificationInput={setClassificationInput}
          onAddClassification={addClassification}
          onRemoveClassification={removeClassification}
          savingClassifications={savingClassifications}
          hasWeights={hasWeights}
          bboxClassNames={bboxClassNames}
          visibleBboxClasses={visibleBboxClasses}
          onToggleBboxClass={toggleBboxClass}
          onSetAllBboxClassesVisible={setAllBboxClassesVisible}
        />
      )}

      {showSampleData && (
        <SampleImagesSection
          totalDatasetImages={allSampleUrls.length}
          gridSampleUrls={filteredSampleUrls}
          displayedUrls={displayedUrls}
          gridStartIndex={gridStartIndex}
          startIndex={startIndex}
          endIndex={endIndex}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={goToPage}
          useInfiniteScroll={useInfiniteScroll}
          setUseInfiniteScroll={setUseInfiniteScroll}
          imageContainerRef={imageContainerRef}
          overlay={overlay}
          onToggleOverlay={toggleOverlay}
          hasWeights={hasWeights}
          weightIndex={weightIndexLoading ? null : weightIndex}
          bboxIndex={bboxIndexLoading ? null : bboxIndex}
          classificationIndex={effectiveClassificationIndex}
          selectedUrlsForDisplay={overlay.classifications ? selectedImageUrls : new Set()}
          onImageSelect={handleImageSelect}
          onImageClick={loadImageUrl}
          thumbnailDisplayMode={thumbnailDisplayMode}
          apiBaseUrl={getDataviewerApiBaseUrl()}
          presignedThumbMap={dvcPresignedThumbMap}
          showBatchBar={overlay.classifications && selectedImageUrls.size > 0}
          classificationBatchInput={classificationBatchInput}
          onClassificationBatchInputChange={setClassificationBatchInput}
          onAddClassificationsBatch={handleAddClassificationsBatch}
          addingClassificationsBatch={addingClassificationsBatch}
          classificationToRemove={classificationToRemove}
          onClassificationToRemoveChange={setClassificationToRemove}
          selectedImagesClassificationsUnion={selectedImagesClassificationsUnion}
          onRemoveClassificationBatch={handleRemoveClassificationBatch}
          removingClassification={removingClassification}
          onDeselectAll={() => setSelectedImageUrls(new Set())}
          classificationFilterAvailable={allClassificationNames}
          classificationFilterSelected={selectedClassificationFilters}
          onClassificationFilterChange={setSelectedClassificationFilters}
          classificationIndexLoading={classificationIndexLoading}
          classificationIndexError={classificationIndexError || undefined}
          includeLabelsFromCsv={includeLabelsFromCsv}
          includeLabelsFromDb={includeLabelsFromDb}
          onIncludeLabelsFromCsvChange={onIncludeLabelsFromCsvChange}
          onIncludeLabelsFromDbChange={onIncludeLabelsFromDbChange}
        />
      )}

      {showBatchProcessing && (
        <BatchProcessingPanel
          batchSize={batchSize}
          onBatchSizeChange={setBatchSize}
          onStartBatch={startBatchProcessing}
          onStopBatch={stopBatchProcessing}
          batchProgress={batchProgress}
        />
      )}
    </div>
  );
};

export default DVCViewer;
